/**
 * THE WORDS OF THE SETTING, as a player reads them.
 *
 * ⭐ **THIS IS NOT A COPY OF `docs/LORE.md`, AND THE DISTINCTION IS LOAD-BEARING.** That file is the
 * DESIGN owner: it argues why the setting is a collision rather than a western, measures the drift
 * across the card set, records what we take from comparable games, and names one of our own
 * properties. **Most of it must never reach a player** - it is reasoning about the world, not the
 * world. This file is the authored telling, written FROM that one, the way card flavour text is.
 *
 * ⛔ **SO DO NOT "SYNC" THEM.** A generator that lifted blocks out of `LORE.md` would eventually
 * publish an internal note, and the failure would be silent and permanent - the words are already in
 * a player's browser by the time anyone notices. If the setting changes, `LORE.md` changes first and
 * a human rewrites this.
 *
 * ⭐ **IT IS A MODULE RATHER THAN MARKUP IN THE SCREEN BECAUSE THE VOICE-OVER NEEDS THE SAME WORDS.**
 * A narrated lore page and a written one that disagree is a defect nobody can see in either half.
 */

export interface LoreStrand {
  /** Where it came from, in the player's language - never a scholarly label. */
  readonly name: string;
  /** What it gave the world, in one line. */
  readonly body: string;
  /** The single word that makes it distinct. Shown as a tag. */
  readonly texture: string;
}

/** The opening. Short on purpose: it is the only paragraph everybody reads. */
export const LORE_OPENING = [
  "North American folklore is not one tradition. It is a collision.",
  "Old superstitions crossed the ocean in the heads of people who could not read, met a continent " +
    "nothing in them described, met each other, and met the traditions already here. What came out " +
    "of that is hybrid, and it exists nowhere else.",
  "That collision is the Longwake. It is why nothing out here agrees with anything else, and why " +
    "this is not a western.",
] as const;

export const LORE_STRANDS: readonly LoreStrand[] = Object.freeze([
  {
    name: "The mountain settlers",
    body: "Scots and Irish brought fairies, funeral custom and a reverence for particular dates. " +
      "In the mountains the fairies soured into haints - the unsettled dead - and the granny " +
      "witch became the woman a holler went to for a birth, a burn or a curse.",
    texture: "the named dead",
  },
  {
    name: "The barn painters",
    body: "German settlers carried charm-work into the same valleys: signs painted to keep a thing " +
      "out, words that work because they were said correctly. Not spirits. Geometry.",
    texture: "written protection",
  },
  {
    name: "The canoe crews",
    body: "French lumberjacks struck a deal with the devil to fly a canoe home to their women, on " +
      "terms: do not blaspheme, do not touch a steeple, be back before six. Every bargain out " +
      "here still comes with conditions, and they are always the part that kills you.",
    texture: "the deal, and its terms",
  },
  {
    name: "The holler",
    body: "Where all of it fused. Mountain magic, plant knowledge, the boo-hag and the Wampus cat - " +
      "none of them belonging to any one people any more.",
    texture: "local, poor, haunted",
  },
  {
    name: "The bayou",
    body: "Down the Mississippi the same stories arrived and changed. The wolf-cursed man became " +
      "the Rougarou, prowling the water. The city buries its dead above ground, and they do not " +
      "always stay put.",
    texture: "heat, water, consequence",
  },
  {
    name: "The winter camps",
    body: "Up the valley, Irish and Scots met the French in the shanties: men far from home, snow " +
      "to the eaves, and a long way to travel before the thaw.",
    texture: "distance, and men far from home",
  },
]);

/**
 * The signature of the setting, and the reason it is worth having.
 *
 * ⭐ The same spirit appears twice, carried by the same people, and turned malevolent on the way
 * south. One creature, two realms, different behaviour - the collision made visible in a single
 * thing, and something no western can do.
 */
export const LORE_FEU_FOLLET = {
  title: "The light over the water",
  body: "In the north it is a wisp - a cold light, a nuisance, a soul with unfinished business. " +
    "Carried south by the same French who named it, it turned. In the swamp it waits for a boat " +
    "to follow it, leads it deeper, and lets the water do the rest. The same light. The same name. " +
    "It simply learned something on the way down.",
} as const;

/** The figure at the centre of it. A western has a gunslinger; we have this instead. */
export const LORE_FIGURE = {
  title: "The runner of woods",
  body: "He went past the last fence without a licence and answered to nobody. He wintered with " +
    "people the settlement was afraid of, learned their routes and their languages, and came back " +
    "speaking in a way his own kind did not follow. He belongs to neither side and both sides know " +
    "it. He is the only one who knows what is actually out there.",
} as const;

/**
 * One line per realm, keyed on the realm ID the rest of the client already uses.
 *
 * ⚠️ **KEYED, NOT LISTED.** The set of realms is owned by the generated data (`REALM_COLOUR`), so
 * this map is looked UP rather than iterated. A realm added there and missing here renders without a
 * line instead of vanishing from the page - and a realm removed there cannot leave an orphan here.
 */
export const REALM_LINES: Readonly<Record<string, string>> = Object.freeze({
  Domain_of_Negafook: "The frozen north. Cold enough that the dead keep, and patient enough to wait.",
  The_Verdant_Hollow: "Deep green and older than any of the settlements in it. It was here first.",
  The_Scorched_Expanse: "Heat, dust and open ground. Nothing hides out here, which is its own problem.",
  The_Spirit_Veil: "The thin place. What is on the other side can see through, and occasionally reaches.",
  The_Ironworks: "Furnaces, rails and company towns. The one power out here that does not need a spirit.",
});
